import React, { useEffect, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CalendarIcon, CheckCircle, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { AvailableDate, useSessionDates } from "@/hooks/useSessionDates"
import { scheduleOrientation, scheduleVehicleCollection } from "@/services/sessionService"

import DateTimeSelector from "./DateTimeSelector"

interface ScheduleDialogProps {
  isOpen: boolean
  onClose: () => void
  applicationUUID: string
  applicationId: string
  jobId?: string
  sessionType: "orientation" | "vehicle_collection"
  isRescheduling?: boolean
}

const ScheduleDialog = ({
  isOpen,
  onClose,
  applicationUUID,
  applicationId,
  jobId,
  sessionType,
  isRescheduling = false,
}: ScheduleDialogProps) => {
  const queryClient = useQueryClient()
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<AvailableDate | null>(null)
  const { data: availableDates = [], isLoading, error } = useSessionDates(jobId, sessionType)

  const scheduleMutation = useMutation({
    mutationFn: async (timeSlot: AvailableDate) => {
      if (!jobId) throw new Error("Missing required information for scheduling.")

      const dateObj = new Date(timeSlot.date)
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, "0")
      const day = String(dateObj.getDate()).padStart(2, "0")
      const hours = String(dateObj.getHours()).padStart(2, "0")
      const minutes = String(dateObj.getMinutes()).padStart(2, "0")
      const seconds = String(dateObj.getSeconds()).padStart(2, "0")
      const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`

      const request = {
        jobId,
        applicationId,
        sessionId: timeSlot.sessionId,
        date: formattedDate,
      }

      const response =
        sessionType === "orientation"
          ? await scheduleOrientation(request)
          : await scheduleVehicleCollection({ ...request, applicationId: applicationUUID })

      if (!response.success) {
        throw new Error(response.error || "Failed to schedule session.")
      }

      return response
    },
    onSuccess: (data) => {
      toast.success("Success", {
        description: data.message || "Session scheduled successfully.",
      })
      queryClient.invalidateQueries({ queryKey: ["applicationDetails", applicationUUID] })
      queryClient.invalidateQueries({ queryKey: ["sessionDates", jobId, sessionType] })
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      onClose()
    },
    onError: (error: Error) => {
      toast.error("Error", {
        description: error.message || "An unexpected error occurred. Please try again.",
      })
    },
  })

  useEffect(() => {
    if (error) {
      toast.error("Error", {
        description: "Failed to fetch available dates. Please try again.",
      })
    }
  }, [error])

  // Convert AvailableDate[] to Date[] for DateTimeSelector
  const availableDateObjects = availableDates.map((date) => {
    // Parse the date string to a Date object
    return date.date
  })

  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    setSelectedTimeSlot(null) // Reset time slot when date changes
  }

  // Handle time slot selection
  const handleTimeSlotSelect = (fullDate: Date) => {
    // Find the matching available date from our original data
    setSelectedDate(fullDate)
    // Compare using date strings to avoid timezone issues
    const matchingTimeSlot = availableDates.find((date) => {
      const dateA = date.date
      const dateB = fullDate
      return (
        dateA.getFullYear() === dateB.getFullYear() &&
        dateA.getMonth() === dateB.getMonth() &&
        dateA.getDate() === dateB.getDate() &&
        dateA.getHours() === dateB.getHours() &&
        dateA.getMinutes() === dateB.getMinutes()
      )
    })

    if (matchingTimeSlot) {
      // Create a new object with the same structure to avoid type issues
      const safeTimeSlot: AvailableDate = {
        date: matchingTimeSlot.date,
        sessionId: matchingTimeSlot.sessionId,
        startTime: matchingTimeSlot.startTime,
        endTime: matchingTimeSlot.endTime,
        depot: matchingTimeSlot.depot,
        locationAddress: matchingTimeSlot.locationAddress,
      }
      setSelectedTimeSlot(safeTimeSlot)
    }
  }

  // Get location text for the selected depot
  const getLocationText = () => {
    if (!selectedTimeSlot?.depot) return ""

    const address =
      selectedTimeSlot.locationAddress ||
      [selectedTimeSlot.depot.address, selectedTimeSlot.depot.city].filter(Boolean).join(", ")

    return [selectedTimeSlot.depot.name, address].filter(Boolean).join(", ")
  }

  const handleReschedule = () => {
    scheduleMutation.reset()
    setSelectedDate(undefined)
    setSelectedTimeSlot(null)
  }

  const handleScheduleSession = () => {
    if (!selectedTimeSlot) return
    scheduleMutation.mutate(selectedTimeSlot)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isRescheduling) {
          setSelectedDate(undefined)
          setSelectedTimeSlot(null)
          scheduleMutation.reset()
        }
        onClose()
      }}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {scheduleMutation.isSuccess
              ? "Session Scheduled!"
              : `Select Date and Time for ${
                  sessionType === "orientation" ? "Orientation" : "Vehicle Collection"
                }`}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading available dates...</span>
          </div>
        ) : scheduleMutation.isSuccess && !isRescheduling ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
            <h3 className="text-xl font-medium mb-2">
              {sessionType === "orientation" ? "Orientation" : "Vehicle Collection"} Scheduled
              Successfully
            </h3>
            <p className="text-muted-foreground mb-4">
              {selectedTimeSlot?.depot?.name}, {/* Format date without timezone conversion */}
              {(() => {
                if (!selectedTimeSlot?.date) return ""
                const date = new Date(selectedTimeSlot.date)
                // Check if date is valid
                if (isNaN(date.getTime())) return "Invalid date"

                const days = [
                  "Sunday",
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                ]
                const months = [
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ]

                // Format date using direct property access
                const day = date.getDate()
                const month = date.getMonth()
                const year = date.getFullYear()
                const weekday = date.getDay()

                return `${days[weekday]}, ${months[month]} ${day}, ${year}`
              })()}
              <br />
              {selectedTimeSlot?.startTime} - {selectedTimeSlot?.endTime}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {isRescheduling && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
                <h3 className="text-amber-800 font-medium">Rescheduling Session</h3>
                <p className="text-amber-700 text-sm">
                  Please select a new date and time for your{" "}
                  {sessionType === "orientation" ? "orientation" : "vehicle collection"} session.
                </p>
              </div>
            )}
            {availableDates.length === 0 ? (
              <div className="text-center py-6">
                <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No available dates found.</p>
              </div>
            ) : (
              <DateTimeSelector
                availableDates={availableDateObjects}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                onTimeSelect={handleTimeSlotSelect}
                title={sessionType === "orientation" ? "Orientation" : "Vehicle Collection"}
                locationText={getLocationText()}
              />
            )}
          </div>
        )}

        <DialogFooter>
          {scheduleMutation.isSuccess && !isRescheduling ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReschedule}>
                Reschedule
              </Button>
              <Button onClick={onClose}>Close</Button>
            </div>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={scheduleMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleScheduleSession}
                disabled={!selectedTimeSlot || scheduleMutation.isPending}
                className="ml-2"
              >
                {scheduleMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isRescheduling ? "Rescheduling..." : "Scheduling..."}
                  </>
                ) : isRescheduling ? (
                  "Confirm New Time"
                ) : (
                  "Schedule Session"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ScheduleDialog
